/* ============================================
   SEEK KEY — Shopify → Checkout Node.js Redirect
   ============================================
   
   v3 (07/03/2026):
   - Migrado de WooCommerce pra Checkout Node.js
   - Preços em centavos (Shopify nativo), checkout divide por 100
   - "Comprar agora" limpa carrinho e envia SÓ o produto clicado
   - "Finalizar compra" envia o carrinho inteiro
   
   INSTALAÇÃO:
   - Upload em: Shopify Admin → Themes → Edit Code → Assets
   - No layout/theme.liquid, antes do </body>:
     {{ 'seekkey-checkout-redirect.js' | asset_url | script_tag }}
   ============================================ */

(function() {
  'use strict';

  const CONFIG = {
    checkoutUrl: 'https://secure.seekkey.com.br',
    debug: false
  };

  let isRedirecting = false;

  // Reset flag when page is restored from bfcache (back button)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      isRedirecting = false;
    }
  });

  // Checkout gate: check if user is logged in
  function isLoggedIn() {
    return document.cookie.indexOf('sk_customer=') !== -1;
  }

  function redirectToLogin(cartBase64) {
    var loginUrl = CONFIG.checkoutUrl + '/conta/login?redirect=checkout';
    if (cartBase64) loginUrl += '&cart=' + encodeURIComponent(cartBase64);
    window.location.href = loginUrl;
  }

  function resetRedirectState() {
    isRedirecting = false;
  }

  // ══════════════════════════════════════
  // BUSCAR CARRINHO
  // ══════════════════════════════════════
  async function getShopifyCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Erro ao buscar carrinho');
      return await response.json();
    } catch (error) {
      console.error('[SeekKey] Erro:', error);
      return null;
    }
  }

  // ══════════════════════════════════════
  // MONTAR URL DO CHECKOUT (base64)
  // ══════════════════════════════════════
  function buildCheckoutUrl(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return null;

    const payload = {
      token: cart.token,
      item_count: cart.item_count,
      total_price: cart.total_price,
      items: cart.items.map(function(item) {
        // Defense-in-depth: força tipos numéricos pra prevenir injection no checkout destino
        var qty = parseInt(item.quantity, 10);
        if (!Number.isFinite(qty) || qty < 1) qty = 1;
        if (qty > 50) qty = 50;
        var price = parseInt(item.price, 10);
        if (!Number.isFinite(price) || price < 0) price = 0;
        var linePrice = parseInt(item.line_price, 10);
        if (!Number.isFinite(linePrice) || linePrice < 0) linePrice = price * qty;
        return {
          id: item.id,
          variant_id: item.variant_id,
          product_id: item.product_id,
          title: item.title,
          product_title: item.product_title,
          variant_title: item.variant_title,
          product_type: item.product_type,
          sku: item.sku,
          quantity: qty,
          price: price,
          line_price: linePrice,
          image: item.image,
          handle: item.handle,
          vendor: item.vendor
        };
      })
    };

    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));

    // Forward UTMs + click IDs da Shopify pro checkout. Sem isso, 99% dos
    // begin_checkout chegam como (direct) no Supabase — atribuição quebrada.
    // Inclui gclid/fbclid/ttclid/msclkid pra conversão API server-side.
    const forwardKeys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid','ttclid','msclkid'];
    const currentParams = new URLSearchParams(window.location.search);
    const extras = forwardKeys
      .filter(function(k) { return currentParams.get(k); })
      .map(function(k) { return '&' + k + '=' + encodeURIComponent(currentParams.get(k)); })
      .join('');

    // Guest checkout: vai direto pro checkout sem exigir login
    // Se o usuário já estiver logado, o checkout detecta via cookie sk_token e preenche dados
    return `${CONFIG.checkoutUrl}/checkout?cart=${encodeURIComponent(encoded)}${extras}`;
  }

  // ══════════════════════════════════════
  // "FINALIZAR COMPRA" — Envia carrinho inteiro
  // ══════════════════════════════════════
  function redirectFromCart() {
    isRedirecting = true;

    if (CONFIG.debug) console.log('[SeekKey] Finalizar compra interceptado');

    getShopifyCart().then(cart => {
      const url = buildCheckoutUrl(cart);
      if (url) {
        window.location.href = url;
      } else {
        resetRedirectState();
        alert('Seu carrinho está vazio!');
      }
    }).catch(() => {
      resetRedirectState();
      alert('Erro ao processar. Tente novamente.');
    });
  }

  // ══════════════════════════════════════
  // "COMPRAR AGORA" — Adiciona ao carrinho (sem redirecionar)
  // ══════════════════════════════════════
  function buyNowAndRedirect() {
    if (CONFIG.debug) console.log('[SeekKey] Comprar agora → adicionar ao carrinho');

    // Pega o formulário do produto
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      if (CONFIG.debug) console.warn('[SeekKey] Form não encontrado');
      resetRedirectState();
      return;
    }

    // Pega variant_id e quantidade do formulário
    const formData = new FormData(form);
    let variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;

    if (!variantId) {
      const idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (idInput) variantId = idInput.value;
    }

    if (!variantId) {
      if (CONFIG.debug) console.warn('[SeekKey] variant_id não encontrado');
      resetRedirectState();
      return;
    }

    // Adiciona ao carrinho e abre o drawer/notificação
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: parseInt(variantId),
        quantity: parseInt(quantity)
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Erro ao adicionar');
      return response.json();
    })
    .then(() => {
      resetRedirectState();
      // Trigger Shopify cart update (opens drawer if theme supports it)
      if (typeof window.Shopify !== 'undefined' && window.Shopify.theme) {
        // Refresh cart sections
        fetch('/?sections=cart-icon-bubble,cart-drawer')
          .then(r => r.json())
          .then(sections => {
            for (var key in sections) {
              var el = document.getElementById('shopify-section-' + key);
              if (el) el.outerHTML = sections[key];
            }
          }).catch(function(){});
      }
      // Fallback: reload page to show updated cart
      window.location.reload();
    })
    .catch(error => {
      console.error('[SeekKey] Erro ao adicionar:', error);
      resetRedirectState();
    });
  }

  // ══════════════════════════════════════
  // INICIALIZAÇÃO
  // ══════════════════════════════════════
  function init() {
    if (CONFIG.debug) console.log('[SeekKey] Redirect v3 inicializado');

    // Seletores de "Finalizar compra" (no carrinho/drawer)
    const checkoutSelectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      '[type="submit"][name="checkout"]',
      'a[href="/checkout"]',
      'a[href*="/checkout"]',
      '.cart__checkout-button',
      '#checkout',
      '#CartDrawer-Checkout',
      '[data-action="checkout"]',
      '.cart-checkout-button'
    ];

    // Seletores de "Comprar agora" (na página do produto)
    const buyNowSelectors = [
      '.shopify-payment-button button',
      '.shopify-payment-button__button',
      '.shopify-payment-button__button--unbranded',
      '.dynamic-checkout__content button',
      '[data-testid="Checkout-button"]'
    ];

    // Interceptar cliques
    document.addEventListener('click', function(event) {
      const target = event.target;

      // Check if click matches any checkout/buy-now selector FIRST
      var isCheckout = false;
      var isBuyNow = false;

      for (const selector of checkoutSelectors) {
        if (target.closest(selector)) { isCheckout = true; break; }
      }
      if (!isCheckout) {
        for (const selector of buyNowSelectors) {
          if (target.closest(selector)) { isBuyNow = true; break; }
        }
      }

      // If it matches, ALWAYS block native behavior — even if already redirecting
      if (isCheckout || isBuyNow) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (isRedirecting) return;

        if (isCheckout) {
          var el = target.closest('a[href]');
          if (el && el.href && el.href.indexOf(CONFIG.checkoutUrl) === 0) return;
          redirectFromCart(event);
        } else {
          buyNowAndRedirect(event);
        }
      }
    }, true);

    // Interceptar submits — always block checkout forms
    document.addEventListener('submit', function(event) {
      const form = event.target;
      const action = form.action || '';
      var submitterName = event.submitter ? (event.submitter.name || '') : '';

      // Any form submission with name="checkout" or action to /checkout
      var isCheckoutSubmit = action.includes('/checkout') || submitterName === 'checkout';

      if (isCheckoutSubmit) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (!isRedirecting) redirectFromCart(event);
        return;
      }

      // Form de "Comprar agora" (POST /cart/add com submitter buy now)
      if (event.submitter && action.includes('/cart/add')) {
        for (const selector of buyNowSelectors) {
          if (event.submitter.closest(selector)) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            if (!isRedirecting) buyNowAndRedirect(event);
            return;
          }
        }
      }
    }, true);

    // Interceptar fetch pro /checkout
    const originalFetch = window.fetch;
    window.fetch = function() {
      const url = arguments[0];
      if (typeof url === 'string' && url.indexOf('/checkout') !== -1 && url.indexOf(CONFIG.checkoutUrl) === -1) {
        if (isRedirecting) return new Promise(function() {});
        if (CONFIG.debug) console.log('[SeekKey] Fetch /checkout interceptado');
        isRedirecting = true;
                getShopifyCart().then(cart => {
          const checkoutUrl = buildCheckoutUrl(cart);
          if (checkoutUrl) window.location.href = checkoutUrl;
          else resetRedirectState();
        });
        return new Promise(function() {});
      }
      return originalFetch.apply(this, arguments);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
